import React, { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAccount } from 'wagmi';
import Draw from './utils/Draw';
import socketService from './services/socketService';
import LoadingScreen from './components/LoadingScreen';
import useAppStore from './stores/appStore';

function App({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isGamePage = location.pathname.startsWith('/game-lobby/game');
  const { address, isConnected } = useAccount();
  
  
  // Zustand store
  const {
    connectionStatus,
    connectionError,
    showLoadingScreen,
    setConnectionStatus,
    setConnectionError,
    setBoard,
    setDraw,
    addMessage,
    setCurrentRoom,
    setShowLoadingScreen,
    initializeSocket,
    initializeFromStorage,
  } = useAppStore();

  // Detect Brave browser
  const isBrave = navigator.brave && navigator.brave.isBrave;

  // Initialize app on mount

  useEffect(() => {
    const initialize = async () => {
      try {
        // Initialize from localStorage first
        initializeFromStorage();
        
        // Get wallet address from Wagmi
        const walletAddress = isConnected ? address : null;
        
        // Initialize socket
        await initializeSocket(walletAddress, isBrave);
        
        // Removed: All session recovery logic
        
      } catch (error) {
        console.error('❌ Failed to initialize app:', error);
      }
    };

    initialize();

    return () => {
      socketService.disconnect();
    };
  }, [initializeSocket, initializeFromStorage, address, isConnected, isBrave, navigate]);

  // Socket event handlers
  const handleConnectionStatus = useCallback((status) => {
    if (connectionStatus !== 'connected' || status === 'error' || status === 'disconnected') {
      setConnectionStatus(status);
    }
    if (status === 'connected') {
      setConnectionError('');
    }
  }, [connectionStatus, setConnectionStatus, setConnectionError]);

  const handleLoadingComplete = useCallback(() => {
    setShowLoadingScreen(false);
  }, [setShowLoadingScreen]);

  const handleConnectionError = useCallback((error) => {
    setConnectionError(error);
  }, [setConnectionError]);

  const handleRoomInvite = useCallback((roomId) => {
    setCurrentRoom(roomId);
    navigate('/game-lobby/lobby');
  }, [navigate, setCurrentRoom]);

  const handleGameStart = useCallback(({ roomId, rejoin }) => {
    navigate(roomId ? `/game-lobby/game/${roomId}` : '/game-lobby/game');

    if (rejoin) {
      console.log('🔄 Rejoining game session');
    }
  }, [navigate]);

  // Removed: Session recovery handlers

  const handleRoomAssigned = useCallback((roomId) => {
    setCurrentRoom(roomId);
  }, [setCurrentRoom]);

  const handleRoomOver = useCallback((message) => {
    navigate('/game-lobby/lobby');
    setCurrentRoom('');
  }, [navigate, setCurrentRoom]);

  const handleBoardUpdate = useCallback((newBoard) => {
    setBoard(newBoard);
  }, [setBoard]);

  const handleMessage = useCallback((message) => {
    addMessage(message);
  }, [addMessage]);

  const handleGracePeriodStarted = useCallback((message) => {
    console.log('⏰ DEBUG: Grace period started', { message });
    // You can add UI feedback here if needed
  }, []);

  useEffect(() => {
    // Set up socket service event listeners

    // Add event listeners
    socketService.addEventListener('connectionStatus', handleConnectionStatus);
    socketService.addEventListener('connectionError', handleConnectionError);
    socketService.addEventListener('roomInvite', handleRoomInvite);
    socketService.addEventListener('gameStart', handleGameStart);
    socketService.addEventListener('roomAssigned', handleRoomAssigned);
    socketService.addEventListener('roomOver', handleRoomOver);
    socketService.addEventListener('boardUpdate', handleBoardUpdate);
    socketService.addEventListener('message', handleMessage);
    socketService.addEventListener('gracePeriodStarted', handleGracePeriodStarted);

    return () => {
      // Clean up event listeners
      socketService.removeEventListener('connectionStatus', handleConnectionStatus);
      socketService.removeEventListener('connectionError', handleConnectionError);
      socketService.removeEventListener('roomInvite', handleRoomInvite);
      socketService.removeEventListener('gameStart', handleGameStart);
      socketService.removeEventListener('roomAssigned', handleRoomAssigned);
      socketService.removeEventListener('roomOver', handleRoomOver);
      socketService.removeEventListener('boardUpdate', handleBoardUpdate);
      socketService.removeEventListener('message', handleMessage);
      socketService.removeEventListener('gracePeriodStarted', handleGracePeriodStarted);
    };
  }, [
    handleConnectionStatus,
    handleConnectionError,
    handleRoomInvite,
    handleGameStart,
    handleRoomAssigned,
    handleRoomOver,
    handleBoardUpdate,
    handleMessage,
    handleGracePeriodStarted,
  ]);

  // Initialize Draw when navigating to game
  useEffect(() => {
    if (location.pathname.startsWith('/game') && !useAppStore.getState().draw) {
      setDraw(new Draw());
    }
  }, [location.pathname, setDraw]);

  // All app actions are now handled by useAppStore

  // Show loading screen on initial load
  if (showLoadingScreen && !isGamePage) {
    return <LoadingScreen onLoadingComplete={handleLoadingComplete} />;
  }

  // Only show error state if there's actually an error
  if (connectionStatus === 'error') {
    return (
      <div className='connection-status error'>
        <h2>Connection Error</h2>
        <p>{connectionError}</p>
        <p>Please make sure your backend server is running</p>
        <button onClick={() => window.location.reload()}>Retry Connection</button>
      </div>
    );
  }

  // Show simple loading only during initial setup (very brief)
  if (connectionStatus === 'initializing') {
    return (
      <div className='connection-status'>
        <h2>Bomberman Hunt</h2>
        <p>Starting game...</p>
        {/* No spinner for Brave, simple text for others */}
        {!isBrave && <div className='loading-spinner'></div>}
      </div>
    );
  }

  // No need for Context Provider - Zustand works globally
  return <>{children}</>;
}

export default App;
